import useSWR from "swr";
import { DatabaseManager } from "./DatabaseManager";
import { ADMIN_INSULIN_PATH, ADMIN_LINK_PATH, PARTICIPANT_PATH, PATIENT_REVIEW, PARTICIPANT_ORPHANS, PATIENT_SEARCH, ADMIN_ALERTS_PATH, ADMIN_ALERT_PATH } from "../util/const";
import { Patient, ReviewWrapper } from "../types/patient";
import { Target } from "../types/target";
import { patientListKey } from "../util/patientListKey";


// This does a fetch before render so we can avoid using all that useEffect, useCallback stuff.
const fetcher = (url: string) => DatabaseManager.getInstance().get(url).then(({data}) => data);

// The active list suspends so the dashboard's Suspense boundary shows a spinner
// until it arrives. It is the only list requested when the portal opens.
export const useActivePatients = () => {
  const { data, error, isLoading, mutate } = useSWR(patientListKey(true, true), fetcher, { suspense: true })
  return {
    patients: data as Patient[],
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

// The archived list is not requested until enabled, and never suspends, so it
// cannot hold up the active tab. Until it has loaded the list is empty.
export const useArchivedPatients = (enabled: boolean) => {
  const { data, error, isLoading, mutate } = useSWR(patientListKey(false, enabled), fetcher)
  return {
    patients: (data ?? []) as Patient[],
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const useOrphanParticipants = () => {  
  const { data, error, isLoading, mutate } = useSWR(PARTICIPANT_ORPHANS, fetcher, { suspense: true})
  return {
    participants: data,
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const useParticipant = (resourceId: string) => {
  const { data, error, isLoading, mutate } = useSWR(PARTICIPANT_PATH + resourceId, fetcher)
  return {
    participant: data,
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const usePatientReview = (args: object) => {
  const { data, error, isLoading, isValidating, mutate } = useSWR([PATIENT_REVIEW, args], ([url, args]) => DatabaseManager.getInstance().getWithArgs(url, args).then(({data}) => data), {keepPreviousData: true});
  return {
    review: data as ReviewWrapper,
    isLoading: isLoading,
    isValidating: isValidating,
    isError: error,
    mutate
  }
}

export const usePatientSearch = (args: object) => {
  const { data, isLoading, error } = useSWR([PATIENT_SEARCH, args], ([url, args]) => DatabaseManager.getInstance().getWithArgs(url, args).then(({data}) => data));
  return {
    patients: data,
    isLoading: isLoading,
    isError: error
  }
}

export const useLinks = () => {  
  const { data, error, isLoading, mutate } = useSWR(ADMIN_LINK_PATH, fetcher, { suspense: true })
  return {
    links: data,
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const useInsulins = () => {  
  const { data, error, isLoading, mutate } = useSWR(ADMIN_INSULIN_PATH, fetcher, { suspense: true })
  return {
    insulins: data,
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const useAlertSettings = () => {  
  const { data, error, isLoading, mutate } = useSWR(ADMIN_ALERTS_PATH, fetcher, { suspense: true })
  return {
    settings: data as Target[],
    isLoading: isLoading,
    isError: error,
    mutate
  }
}

export const useAlertSetting = (diabetesType: string) => {  
  const { data, error, isLoading, mutate } = useSWR(ADMIN_ALERT_PATH+ diabetesType, fetcher, { suspense: true })
  return {
    settings: data,
    isLoading: isLoading,
    isError: error,
    mutate
  }
}